import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

Chart.register(...registerables);

interface JobWageMedianTrendComparisonProps {
  location: string;
}

const MAIN_SECTORS = [
  'Management, business, science, and arts occupations',
  'Service occupations',
  'Sales and office occupations',
  'Natural resources, construction, and maintenance occupations',
  'Production, transportation, and material moving occupations'
];

type WageRow = {
  year: number | string;
  job_type: string | null;
  median_income: number | string | null;
  gender: string | null;
};

type Series = {
  label: string;
  color: string;
  valuesByYear: Record<number, number>;
};

const normalizeGender = (value: string | null | undefined) => (value || '').trim().toLowerCase();

const toNumber = (value: number | string | null | undefined) => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const median = (values: number[]) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid];
  return (sorted[mid - 1] + sorted[mid]) / 2;
};

const buildMedianSeries = (rows: WageRow[], label: string, color: string): Series => {
  const byYear: Record<number, WageRow[]> = {};
  rows.forEach((row: WageRow) => {
    const year = Number(row.year);
    if (!Number.isFinite(year)) return;
    if (!byYear[year]) byYear[year] = [];
    byYear[year].push(row);
  });

  const valuesByYear: Record<number, number> = {};
  Object.keys(byYear).forEach((yearKey) => {
    const year = Number(yearKey);
    const yearRows = byYear[year] || [];
    const hasOverall = yearRows.some((r: WageRow) => {
      const g = normalizeGender(r.gender);
      return g === '' || g === 'both';
    });

    const filtered = yearRows.filter((r: WageRow) => {
      const jobType = (r.job_type || '').trim();
      const gender = normalizeGender(r.gender);
      const isMain = MAIN_SECTORS.includes(jobType);
      const genderMatch = hasOverall ? (gender === '' || gender === 'both') : true;
      return isMain && genderMatch;
    });

    const values = filtered
      .map((r: WageRow) => toNumber(r.median_income))
      .filter((v): v is number => v !== null && v > 0);

    const yearMedian = median(values);
    if (yearMedian !== null) {
      valuesByYear[year] = yearMedian;
    }
  });

  return { label, color, valuesByYear };
};

const JobWageMedianTrendComparison: React.FC<JobWageMedianTrendComparisonProps> = ({ location }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const creatingRef = useRef<boolean>(false);
  const [hasData, setHasData] = React.useState<boolean | null>(null);

  const buildChart = useCallback(async () => {
    if (!canvasRef.current) return;
    if (creatingRef.current) return;
    creatingRef.current = true;

    try {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
      if (existing) existing.destroy();
    } catch {}

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    await supabaseLocationService.initialize();

    const locationGeofips = await supabaseLocationService.getGeofips(location);
    if (!locationGeofips) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    const countyName = supabaseLocationService.getCountyForCity(location);
    const countyGeofips = countyName ? await supabaseLocationService.getGeofips(countyName) : null;
    const stateGeofips = await supabaseLocationService.getGeofips('California');

    const fetchRows = async (geofips: number | null, fallbackName?: string, geographyType?: string) => {
      let query = supabaseClient
        .from('job_by_wage_level')
        .select('year, job_type, median_income, gender');

      if (geofips !== null) {
        query = query.eq('geofips', geofips);
      } else if (fallbackName) {
        query = query.ilike('location_name', fallbackName);
        if (geographyType) {
          query = query.eq('geography_type', geographyType);
        }
      } else if (geographyType) {
        query = query.eq('geography_type', geographyType);
      }

      const { data, error } = await query.order('year', { ascending: true });
      if (error) {
        return [];
      }
      return (data || []) as WageRow[];
    };

    const locationRows = await fetchRows(locationGeofips);
    const countyRows = countyGeofips ? await fetchRows(countyGeofips) : [];
    let stateRows = stateGeofips ? await fetchRows(stateGeofips) : [];
    if (!stateRows.length) {
      stateRows = await fetchRows(null, 'California', 'state');
    }

    if (!locationRows.length && !stateRows.length && !countyRows.length) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    const series: Series[] = [
      buildMedianSeries(locationRows, location, '#1f77b4'),
      buildMedianSeries(stateRows, 'California', '#16a34a')
    ];

    if (countyRows.length && countyName) {
      series.push(buildMedianSeries(countyRows, `${countyName} (County)`, '#f59e0b'));
    }

    const yearsSet = new Set<number>();
    series.forEach(s => {
      Object.keys(s.valuesByYear).forEach(year => yearsSet.add(Number(year)));
    });
    const years = Array.from(yearsSet).sort((a, b) => a - b);

    const datasets = series.map(s => ({
      label: s.label,
      data: years.map(year => s.valuesByYear[year] ?? null),
      borderColor: s.color,
      backgroundColor: 'transparent',
      tension: 0,
      pointRadius: 0,
      borderWidth: 2
    }));

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: { labels: years, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(`Median Wages Over Time - ${location}`),
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (ctx: any) => `${ctx.dataset.label}: $${(ctx.parsed.y || 0).toLocaleString()}`
            }
          }
        },
        scales: {
          y: { title: { display: true, text: 'Median Wages (USD)' }, beginAtZero: true },
          x: { title: { display: true, text: 'Year' } }
        },
        interaction: { mode: 'index', intersect: false }
      }
    });

    setHasData(true);
    creatingRef.current = false;
  }, [location]);

  useEffect(() => {
    buildChart();
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [buildChart]);

  if (hasData === false) {
    return (
      <div className="chart-no-data">
        <p>No wage trend data available for {location}.</p>
      </div>
    );
  }

  return <canvas ref={canvasRef} style={{ width: '100%', height: '320px' }} />;
};

export default JobWageMedianTrendComparison;

